import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';

import { AdmissionApplication } from '../../../types/admissions';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import useTimezone from '../../../hooks/useTimezone';

interface Props {
  open: boolean;
  application: AdmissionApplication;
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}

/** Give a family more time to accept: move the offer's last day later. */
const ExtendOfferDialog: React.FC<Props> = ({ open, application, onCancel, onDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  // Calendar dates in the school's timezone.
  const { getDateFromTimestamp: toInputDate } = useTimezone();
  const current = application.offer ? new Date(application.offer.expiresAt) : new Date();
  const earliest = new Date(Math.max(current.getTime(), Date.now()) + 24 * 60 * 60 * 1000);
  const [until, setUntil] = useState(toInputDate(new Date(earliest.getTime() + 6 * 24 * 60 * 60 * 1000)));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = !!until && until >= toInputDate(earliest);

  const save = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.extendOffer(application._id, until, reason.trim() || undefined);
      enqueueSnackbar(`Offer extended to ${dateTimeUtils.formatDateForDisplay(until)}`, { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not extend the offer', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>Extend offer</Typography>
        <Typography variant="body2" color="text.secondary">
          Currently valid until {dateTimeUtils.formatDateForDisplay(current.toISOString())}.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            type="date"
            size="small"
            label="New last day"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            inputProps={{ min: toInputDate(earliest) }}
            error={!valid}
            helperText={valid ? 'The offer stays open until the end of this day.' : 'Pick a later day than the current one.'}
            InputLabelProps={{ shrink: true }}
          />
          <TextField size="small" label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Extend offer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtendOfferDialog;
